/**
 * Platform-agnostic state + mapping helpers for the skill builder wizard
 * (web dialog today; the native sheet reuses these). Mirrors the two-way
 * manifest contract in docs/skills-two-way-design.md: `kind`/`run`/`cv_config`
 * are composed into SKILL.md server-side from the structured POST/PATCH body,
 * so this module only maps between the wizard draft and that body.
 */
import type {
  CVAccentColor,
  CVLayoutStyle,
  CvSectionId,
  SkillGenerateDraftResult,
  SkillKind,
  SkillRecommendedModel,
} from '@tabbio/types';
import { CV_SECTION_IDS, SKILL_CV_SECTION_PROMPT_MAX } from '@tabbio/utils';
import {
  AGENT_MODEL_RUNTIME_IDS,
  findAgentModel,
  getDefaultAgentModelForPlan,
  type AgentModelEntry,
  type AgentModelTier,
} from '@tabbio/utils/agent-models';
import type {
  CreateSkillInput,
  SkillDetailWithConfig,
} from '../../lib/skills-api';

// Ordered default section vocabulary (the 8 renderable ids; header always renders).
export const BUILDER_SECTION_IDS: readonly CvSectionId[] = CV_SECTION_IDS;

// The sections a fresh CV skill starts with (the reference's default 5). The
// remaining renderable ids (projects, certifications, languages) are offered
// via the "Add section" control on the sections step.
export const CORE_SECTION_IDS: readonly CvSectionId[] = CV_SECTION_IDS.filter(
  (id) => id !== 'projects',
).slice(0, 5);

export const SECTION_PROMPT_MAX = SKILL_CV_SECTION_PROMPT_MAX;
/** Mirrors the API's `skillCreateSchema.instructions` cap. */
export const INSTRUCTIONS_MAX = 20000;

export interface BuilderSectionDraft {
  id: CvSectionId;
  enabled: boolean;
  prompt: string;
}

/**
 * Audience / icon / model options surfaced by the redesigned "New skill" flow.
 * `region` maps to the backed `region` field, `language` to `locales[]`, and
 * `level` is appended to `tags`. `iconKey`/`iconUrl` map to the backed icon
 * columns; `model` persists as `Skill.recommendedModel` via `recommended_model`
 * in the manifest frontmatter.
 */
// No 'flag' here by design: the flag treatment was rejected for the builder,
// and it doubles as the catalog's "pin me to the top" signal. Catalog flag
// rendering (skillCatalogVisual) is unaffected.
export const SKILL_ICON_OPTIONS = [
  'doc',
  'sparkle',
  'briefcase',
  'grad',
  'star',
] as const;
export type SkillBuilderIcon = (typeof SKILL_ICON_OPTIONS)[number];

export const SKILL_REGION_OPTIONS = [
  'UAE',
  'KSA',
  'Kuwait',
  'Qatar',
  'Bahrain',
  'Oman',
  'GCC',
] as const;

export const SKILL_LEVEL_OPTIONS = [
  'intern',
  'fresh-grad',
  'mid-career',
  'executive',
] as const;

export const SKILL_LANGUAGE_OPTIONS = ['en', 'ar', 'bi'] as const;

export type SkillModelTier = AgentModelTier;
export interface SkillModelOption {
  /** The chat catalog id (e.g. `google/gemini-3.6-flash`): what the picker shows and the draft stores. */
  id: string;
  tier: SkillModelTier;
  /** The catalog id this display id persists as (`Skill.recommendedModel`). */
  catalogId: SkillRecommendedModel;
  /** The shared chat catalog entry: canonical name, brand mark, i18n slug. */
  entry: AgentModelEntry;
}

/**
 * Skill catalog id per displayed model. The two vocabularies differ and mixing them
 * is a silent 400: `/api/skills/generate` validates `model` against the skill
 * enum, so sending a chat id fails before the request reaches the service.
 * `gpt` has no chat model any more and stays a legacy stored value only.
 */
const CATALOG_ID_BY_AGENT_MODEL: Record<string, SkillRecommendedModel> = {
  'google/gemini-3.5-flash-lite': 'lite',
  'google/gemini-3.6-flash': 'flash',
  'google/gemini-3.7-flash': 'gpro',
  'anthropic/claude-sonnet-5': 'sonnet',
  'anthropic/claude-opus-5': 'opus',
};

/**
 * Skill execution still has standard and premium policies (3.5 Lite / 3.7).
 * Do not inherit Pro's 3.8 chat choice until skill execution supports it.
 * Claude display choices retain their stored recommendations; both still use
 * the existing premium execution policy.
 */
export const SKILL_MODEL_OPTIONS: readonly SkillModelOption[] = [
  {
    id: AGENT_MODEL_RUNTIME_IDS.geminiFlashLite,
    tier: 'fast',
    catalogId: 'flash',
    entry: {
      ...getDefaultAgentModelForPlan('free'),
      id: AGENT_MODEL_RUNTIME_IDS.geminiFlashLite,
      displayName: 'Gemini 3.5 Flash-Lite',
      slug: 'geminiFlashLite',
    },
  },
  {
    id: AGENT_MODEL_RUNTIME_IDS.geminiPlus,
    tier: 'pro',
    catalogId: 'gpro',
    entry: getDefaultAgentModelForPlan('plus'),
  },
  ...(['sonnet', 'opus'] as const).map((name): SkillModelOption => {
    const entry = findAgentModel(`anthropic/claude-${name}-5`)!;
    return {
      id: entry.id,
      tier: entry.tier,
      catalogId: name,
      entry: { ...entry, runtimeModelId: AGENT_MODEL_RUNTIME_IDS.geminiPlus },
    };
  }),
];

export const DEFAULT_SKILL_MODEL_ID: string =
  AGENT_MODEL_RUNTIME_IDS.geminiFlashLite;

/** Canonical product name for a display id (falls back to the id itself). */
export function skillModelLabel(displayId: string): string {
  return (
    SKILL_MODEL_OPTIONS.find((m) => m.id === displayId)?.entry.displayName ??
    displayId
  );
}

/** Display id → the catalog id the API accepts. Unknown ids fall back to flash. */
export function toCatalogModel(displayId: string): SkillRecommendedModel {
  return (
    SKILL_MODEL_OPTIONS.find((m) => m.id === displayId)?.catalogId ??
    CATALOG_ID_BY_AGENT_MODEL[displayId] ??
    'flash'
  );
}

/** Catalog id → the display id the picker selects, or null if unrecognised. */
export function fromCatalogModel(
  catalogId: string | null | undefined,
): string | null {
  if (!catalogId) return null;
  if (catalogId === 'lite' || catalogId === 'flash')
    return DEFAULT_SKILL_MODEL_ID;
  return (
    SKILL_MODEL_OPTIONS.find((model) => model.catalogId === catalogId)?.id ??
    null
  );
}

/** Pro models are the ones behind the paywall (SKILL_MODEL_POLICY_MAP.premium). */
export function isPremiumModel(displayId: string): boolean {
  return ['sonnet', 'gpro', 'opus'].includes(toCatalogModel(displayId));
}

export interface SkillBuilderDraft {
  name: string;
  description: string;
  nameAr: string;
  descriptionAr: string;
  /** Raw comma-separated input; parsed on submit. */
  tags: string;
  /**
   * Catalog categories (`apply`, `interview`, `cv-writing`, ...). The builder has
   * no field for them: the AI supplies them on generate and an edit keeps what
   * is stored, so a skill never loses the category that picks its glyph.
   */
  categories: string[];
  kind: SkillKind;
  /** Manifest body (portable skills' method; optional for custom CV skills). */
  instructions: string;
  style: CVLayoutStyle;
  color: CVAccentColor;
  showPhoto: boolean;
  /** Full vocabulary in display order; disabled entries are omitted on submit. */
  sections: BuilderSectionDraft[];
  /**
   * Preset skill icon id (persisted as `iconKey`). `flag` is no longer offered
   * by the picker but is kept when a stored skill has it, so an edit does not
   * silently drop the flag pin the author once chose.
   */
  iconKey: SkillBuilderIcon | 'flag';
  /** Uploaded custom icon image URL (persisted as `iconUrl`); '' = none. */
  iconUrl: string;
  /** Audience: region maps to `region`, level to a tag, language to `locales`. */
  region: string;
  level: string;
  language: string;
  /** The recommended model, persisted as `Skill.recommendedModel`. */
  model: string;
  /**
   * What the AI suggested, when it differs from what the user can select.
   * Drives the Pro upsell banner only - never persisted, so the picker can
   * never show a locked row as the current selection.
   */
  recommendedModelSuggestion: string | null;
}

export function defaultBuilderDraft(): SkillBuilderDraft {
  return {
    name: '',
    description: '',
    nameAr: '',
    descriptionAr: '',
    tags: '',
    categories: [],
    kind: 'default',
    instructions: '',
    style: 'MODERN',
    color: 'BLACK',
    showPhoto: true,
    sections: CORE_SECTION_IDS.map((id) => ({
      id,
      enabled: true,
      prompt: '',
    })),
    // Neutral by default. The flag treatment was rejected in review, and it
    // also pinned every new skill to the top of the catalog via skillShowsFlag.
    iconKey: 'doc',
    iconUrl: '',
    region: '',
    level: '',
    language: 'en',
    model: DEFAULT_SKILL_MODEL_ID,
    recommendedModelSuggestion: null,
  };
}

export function parseTagsInput(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[,،]/)
        .map((tag) => tag.trim())
        .filter(Boolean),
    ),
  ).slice(0, 40);
}

/** Enabled sections, in order: what the manifest include-list will contain. */
export function enabledSections(
  draft: SkillBuilderDraft,
): BuilderSectionDraft[] {
  return draft.sections.filter((s) => s.enabled);
}

/** Renderable section ids not yet in the draft: offered by "Add section". */
export function addableSectionIds(draft: SkillBuilderDraft): CvSectionId[] {
  const present = new Set(draft.sections.map((s) => s.id));
  return BUILDER_SECTION_IDS.filter((id) => !present.has(id));
}

/** Maps the wizard draft to the structured POST /api/skills body. */
export function draftToSkillInput(draft: SkillBuilderDraft): CreateSkillInput {
  // Audience is a CV-skill concept: the builder only collects region/level/
  // language for `cv_custom`. A portable skill sends none of it, and the
  // catalog filters treat an undeclared facet as "matches any" so those skills
  // still appear under every chip (see skillMatchesCountry/Level).
  const isCv = draft.kind === 'cv_custom';
  // The chosen level becomes a tag (backed); region + language map to their own
  // backed fields below.
  const tags = parseTagsInput(
    isCv && draft.level ? `${draft.tags},${draft.level}` : draft.tags,
  );
  const input: CreateSkillInput = {
    name: draft.name.trim(),
    description: draft.description.trim(),
    nameAr: draft.nameAr.trim() || undefined,
    descriptionAr: draft.descriptionAr.trim() || undefined,
    instructions: draft.instructions.trim() || undefined,
    tags: tags.length ? tags : undefined,
    region: isCv ? draft.region.trim() || undefined : undefined,
    iconKey: draft.iconKey,
    // '' clears any previously-uploaded image; a URL sets it.
    iconUrl: draft.iconUrl,
    // Persisted as `Skill.recommendedModel`; a default for runs, not a lock.
    recommendedModel: toCatalogModel(draft.model),
    // 'bi' (Bilingual) persists as both locales.
    locales: !isCv
      ? undefined
      : draft.language === 'bi'
        ? ['en', 'ar']
        : draft.language
          ? [draft.language]
          : undefined,
    // Always send the kind discriminator (create AND edit). PATCH merges
    // omitted structured fields from the previous version, so an edit that
    // switches a cv_custom skill back to 'Portable instructions' would
    // silently keep `kind: cv_custom` if we only sent kind for cv_custom.
    // For 'default' we deliberately send no cvConfig.
    kind: draft.kind,
  };
  if (draft.categories.length) input.categories = draft.categories;
  if (draft.kind === 'cv_custom') {
    input.categories = ['cv-writing'];
    input.run = { input: 'cv', output: 'cv_edit' };
    input.cvConfig = {
      style: draft.style,
      color: draft.color,
      showPhoto: draft.showPhoto,
      sections: enabledSections(draft).map((section) => ({
        id: section.id,
        prompt: section.prompt.trim() || undefined,
      })),
    };
  }
  return input;
}

/**
 * Prefills the wizard from a SkillDetail DTO (+ the SKILL.md body from the
 * content endpoint, which is served frontmatter-stripped) for edit mode.
 */
export function draftFromSkillDetail(
  detail: SkillDetailWithConfig,
  body: string,
): SkillBuilderDraft {
  const draft = defaultBuilderDraft();
  draft.name = detail.name ?? '';
  draft.description = detail.description ?? '';
  draft.nameAr = detail.nameAr ?? '';
  draft.descriptionAr = detail.descriptionAr ?? '';
  draft.tags = (detail.tags ?? []).join(', ');
  draft.instructions = body.trim();
  draft.region = detail.region ?? '';
  draft.categories = detail.categories ?? [];
  if (
    detail.iconKey &&
    ((SKILL_ICON_OPTIONS as readonly string[]).includes(detail.iconKey) ||
      detail.iconKey === 'flag')
  ) {
    draft.iconKey = detail.iconKey as SkillBuilderIcon | 'flag';
  }
  draft.iconUrl = detail.iconUrl ?? '';
  draft.model =
    fromCatalogModel(detail.recommendedModel) ?? DEFAULT_SKILL_MODEL_ID;
  if (detail.languages && detail.languages.length > 0) {
    const langs = detail.languages.map((l) => l.toLowerCase());
    draft.language =
      langs.includes('en') && langs.includes('ar') ? 'bi' : detail.languages[0];
  }
  const cvConfig = detail.cvConfig;
  if (detail.kind === 'cv_custom' && cvConfig) {
    draft.kind = 'cv_custom';
    draft.style = cvConfig.style ?? draft.style;
    draft.color = cvConfig.color ?? draft.color;
    draft.showPhoto = cvConfig.showPhoto ?? draft.showPhoto;
    const included = cvConfig.sections ?? [];
    const promptById = new Map(
      included.map((section) => [section.id, section.prompt ?? '']),
    );
    const includedIds = included
      .map((section) => section.id)
      .filter((id): id is CvSectionId =>
        (BUILDER_SECTION_IDS as readonly string[]).includes(id),
      );
    // Only the saved sections appear; the rest stay addable via "Add section".
    draft.sections = includedIds.map((id) => ({
      id,
      enabled: true,
      prompt: promptById.get(id) ?? '',
    }));
  }
  return draft;
}

/**
 * Maps a `POST /api/skills/generate` result onto a builder draft, so "Generate
 * skill" opens the wizard already filled in rather than merely seeding the
 * description. For a CV skill this is what makes each section arrive with an
 * AI-drafted prompt.
 *
 * `base` carries what the describe screen already knows - the preset `kind`
 * and the model the author picked - and wins over the result for those two.
 * The API returns no audience, so a CV draft is seeded with the same market
 * defaults the manual path uses (UAE, fresh grad, English) for the author to
 * change in the wizard, rather than arriving with no country at all.
 */
export function draftFromGeneratedSkill(
  result: SkillGenerateDraftResult,
  base?: Partial<SkillBuilderDraft>,
): SkillBuilderDraft {
  const draft: SkillBuilderDraft = { ...defaultBuilderDraft(), ...base };

  draft.name = result.name ?? '';
  draft.description = result.description ?? '';
  draft.nameAr = result.nameAr ?? '';
  draft.descriptionAr = result.descriptionAr ?? '';
  draft.instructions = (result.instructions ?? '').trim();
  draft.kind = base?.kind ?? result.kind ?? 'default';
  if (base?.model) draft.model = base.model;
  // Tags and categories stay out of the UI but keep flowing: search indexes
  // the tags, the category picks the catalog glyph, and the builder has no
  // field for the author to supply either.
  draft.tags = (result.tags ?? []).join(', ');
  draft.categories = result.categories ?? [];
  if (draft.kind === 'cv_custom') {
    draft.region = draft.region || 'UAE';
    draft.level = draft.level || 'fresh-grad';
    draft.language = draft.language || 'en';
  }

  // The suggestion is recorded separately from the selection - see
  // recommendedModelSuggestion. Callers decide what the picker lands on.
  draft.recommendedModelSuggestion = fromCatalogModel(result.recommendedModel);

  const cv = result.cvConfig;
  if (draft.kind === 'cv_custom' && cv) {
    draft.style = cv.style;
    draft.color = cv.color;
    draft.showPhoto = cv.showPhoto;

    const seen = new Set<CvSectionId>();
    const sections: BuilderSectionDraft[] = [];
    for (const section of cv.sections ?? []) {
      const id = section.id as CvSectionId;
      // Defence in depth: the server normalizes these, but a stale client
      // should still never render a section the CV cannot draw.
      if (!(BUILDER_SECTION_IDS as readonly string[]).includes(id)) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      sections.push({
        id,
        enabled: true,
        prompt: (section.prompt ?? '').slice(0, SECTION_PROMPT_MAX),
      });
    }
    // Only the returned sections are included; the rest stay addable.
    if (sections.length) draft.sections = sections;
  }

  return draft;
}

/**
 * "Write with AI" step 2: turns the generate result into the review draft.
 *
 * The author's step-1 text is the *idea prompt*, not copy: it is passed to
 * `POST /api/skills/generate` and then thrown away, so a skill can never be
 * saved with the raw idea as its description (the bug this replaces). The
 * generated name, description and Arabic pair therefore win outright, along
 * with the method (instructions), tags and categories the form has no field
 * for. The author only carries over the two presentation choices they made
 * before generating - the icon and the recommended model - which is also what
 * makes "Regenerate" keep those edits.
 */
export function mergeGeneratedPortableDraft(
  author: SkillBuilderDraft,
  result: SkillGenerateDraftResult,
): SkillBuilderDraft {
  const generated = draftFromGeneratedSkill(result, { kind: 'default' });
  return {
    // name / description / nameAr / descriptionAr / instructions / tags /
    // categories: all the AI's.
    ...generated,
    kind: 'default',
    iconKey: author.iconKey,
    iconUrl: author.iconUrl,
    model: author.model,
  };
}
